import { getSessionStore } from "app/utils/sessionStore";
import { ProposalTypes } from "app/utils/types";
import {
  createGradient,
  getThemeColors,
  getUSANumberFormat,
  projectionOptions,
  formatToCompactNumber,
} from "app/utils/utils";
import { Chart } from "primereact/chart";
import HexagonImg from "../../images/hexagonImg.png";
import IPPageNumber from "../IPPageNuber";
import { lineChartOptions } from "app/uicomponents/macrocomponents/PlansSummary/config";
import { getColorVariations } from "app/utils/colorUtils";

const bsTableHeaders = [
  { header: "Plan Year" },
  { header: "Shareholder Value" },
  { header: "Annual Growth Rate Projection" },
  { header: "Death Benefit" },
  { header: "Disability Benefit" },
];

const kpiTableHeaders = [
  { header: "Plan Year" },
  { header: "Death Benefit" },
  { header: "Living Benefit" },
  { header: "Disability Benefit" },
  { header: "Key Employee(s) Value" },
];

const IPProjections = ({
  proposalType,
  graphData,
  isNewPage = false,
  pageNumber,
  tableData,
}: {
  proposalType: ProposalTypes;
  graphData?: any;
  isNewPage?: boolean;
  pageNumber: string | number;
  tableData?: any;
}) => {
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor, secondaryColor, ipTooltipColor } = getThemeColors(branding);

  const planYear = graphData?.map((item: any) => item.planYear);

  const deathBenefit = graphData?.map((item: any) => item.deathBenefit);
  // const totalDeathBenefit = deathBenefit?.reduce((acc: any, value: any) => acc + value, 0);

  // const currentYear = new Date().getFullYear();
  const currentYearDeathBenefit = graphData?.[9]?.deathBenefit || 0;
  // graphData?.find((item: any) => item.year === currentYear)?.deathBenefit || 0;

  const disabilityBenefit = graphData?.map((item: any) => item.disabilityBenefit);
  // const totalDisabilityBenefit = disabilityBenefit?.reduce(
  //   (acc: any, value: any) => acc + value,
  //   0,
  // );
  const currentYearDisabilityBenefit = graphData?.[9]?.disabilityBenefit || 0;

  const businessValuation = graphData?.map((item: any) => item.businessValuation);
  // const totalBusinessValuation = businessValuation?.reduce(
  //   (acc: any, value: any) => acc + value,
  //   0,
  // );
  const currentYearBusinessValuation = graphData?.[9]?.businessValuation || 0;

  const ciBenefit = graphData?.map((item: any) => item.ciBenefit);
  // const totalCiBenefit = ciBenefit?.reduce((acc: any, value: any) => acc + value, 0);
  const currentYearCiBenefit = graphData?.[9]?.ciBenefit || 0;

  const thirdChartData = () => {
    if (proposalType === ProposalTypes.KeyPersonInsurance) {
      return {
        label: "Living Benefit",
        total: currentYearCiBenefit,
        chartData: {
          labels: planYear?.slice(0, 10),
          datasets: [
            {
              label: "Living Benefit",
              fill: true,
              borderColor: projectionOptions(primaryColor)[2].indicatorColor,
              backgroundColor: (context: any) =>
                createGradient(context, projectionOptions(primaryColor)[2].indicatorColor),
              yAxisID: "y",
              tension: 0.4,
              data: ciBenefit,
            },
          ],
        },
      };
    }
    return {
      label: "Shareholder Value",
      total: currentYearBusinessValuation,
      chartData: {
        labels: planYear?.slice(0, 10),
        datasets: [
          {
            label: "Shareholder Value",
            fill: true,
            borderColor: projectionOptions(primaryColor)[0].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionOptions(primaryColor)[0].indicatorColor),
            yAxisID: "y",
            tension: 0.4,
            data: businessValuation,
          },
        ],
      },
    };
  };

  const deathBenefitChart = {
    labels: planYear?.slice(0, 10),
    datasets: [
      {
        label: "Death Benefit",
        fill: true,
        borderColor: projectionOptions(primaryColor)[1].indicatorColor,
        backgroundColor: (context: any) =>
          createGradient(context, projectionOptions(primaryColor)[1].indicatorColor),
        yAxisID: "y",
        tension: 0.4,
        data: deathBenefit,
      },
    ],
  };

  const disabilityBenefitChart = {
    labels: planYear?.slice(0, 10),
    datasets: [
      {
        label: "Disability Benefit",
        fill: true,
        borderColor: projectionOptions(primaryColor)[2].indicatorColor,
        backgroundColor: (context: any) =>
          createGradient(context, projectionOptions(primaryColor)[2].indicatorColor),
        yAxisID: "y",
        tension: 0.4,
        data: disabilityBenefit,
      },
    ],
  };

  const tableContent = () => {
    const getVariationColor = getColorVariations(secondaryColor, kpiTableHeaders.length);
    const variationColor = getVariationColor.reverse();
    if (proposalType === ProposalTypes.KeyPersonInsurance) {
      return (
        <table className="w-full border-collapse bg-white">
          <thead>
            <tr className="conthrax-font">
              <td className="bg-gray-200 border-2 border-white" />
              <td colSpan={4} className="text-center py-3 bg-gray-200 border-2 border-white">
                BENEFITS
              </td>
            </tr>
            <tr>
              {kpiTableHeaders.map((item, index) => (
                <th
                  key={item.header}
                  style={{ backgroundColor: variationColor[index], width: "20%" }}
                  className="p-1 text-center text-xs font-semibold text-white border-2 border-white"
                >
                  {item.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData?.map((item: any, index: any) => (
              <tr key={index} style={{ backgroundColor: "#EEEEEE", color: "#000000" }}>
                <td className="p-1 text-sm text-center border-2 border-white">{item.planYear}</td>
                <td className="p-1 text-sm text-center border-2 border-white">
                  ${getUSANumberFormat(item.deathBenefit, false)}
                </td>
                <td className="p-1 text-sm text-center border-2 border-white">
                  ${getUSANumberFormat(item.ciBenefit, false)}
                </td>
                <td className="p-1 text-sm text-center border-2 border-white">
                  ${getUSANumberFormat(item.disabilityBenefit, false)}
                </td>
                <td className="p-1 text-sm text-center border-2 border-white">
                  ${getUSANumberFormat(item.valuation, false)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (proposalType === ProposalTypes.BuySell) {
      const getVariationColorBs = getColorVariations(secondaryColor, bsTableHeaders.length);
      const variationColorBs = getVariationColorBs.reverse();
      return (
        <table className="w-full border-collapse bg-white">
          <thead>
            <tr className="conthrax-font">
              <td className="bg-gray-200 border-2 border-white" />
              <td colSpan={2} className="text-center bg-gray-200 border-2 border-white">
                VALUATIONS
              </td>
              <td colSpan={2} className="text-center py-3 bg-gray-200 border-2 border-white">
                SHAREHOLDER BENEFITS
              </td>
            </tr>
            <tr>
              {bsTableHeaders.map((item, index) => (
                <th
                  key={item.header}
                  style={{ backgroundColor: variationColorBs[index], width: "20%" }}
                  className="p-1 text-center text-xs font-semibold text-white border-2 border-white"
                >
                  {item.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData?.map((item: any, index: any) => (
              <tr key={index} style={{ backgroundColor: "#EEEEEE", color: "#000000" }}>
                <td className="p-1 text-sm text-center border-2 border-white">{item.planYear}</td>
                <td className="p-1 text-sm text-center border-2 border-white">
                  ${getUSANumberFormat(item.businessValuation, false)}
                </td>
                <td className="p-1 text-sm text-center border-2 border-white">
                  {item.annualGrowthRate || 0}%
                </td>
                <td className="p-1 text-sm text-center border-2 border-white">
                  ${getUSANumberFormat(item.deathBenefit, false)}
                </td>
                <td className="p-1 text-sm text-center border-2 border-white">
                  ${getUSANumberFormat(item.disabilityBenefit, false)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return null;
  };

  const renderContent = () => {
    if (isNewPage) {
      return tableContent();
    }
    return (
      <div>
        <h1 className="text-center text-2xl font-bold mb-3 conthrax-font interactive-proposal-text">
          Plan Projections
        </h1>
        <div className="w-full mb-3 bg-white">
          <div className="flex justify-between conthrax-font" style={{ width: "503px" }}>
            <p className="font-bold text-gray-800 text-sm">Death Benefit</p>
            <div className="text-base font-bold text-gray-500">
              ${formatToCompactNumber(currentYearDeathBenefit)}
            </div>
          </div>
          <Chart
            type="line"
            height="200px"
            width="503px"
            data={deathBenefitChart}
            options={lineChartOptions(false, ipTooltipColor, "Plan Year", "Plan Year")}
          />
        </div>

        <div className="w-full mb-3 bg-white">
          <div className="flex justify-between conthrax-font" style={{ width: "503px" }}>
            <p className="font-bold text-gray-800 text-sm">Disability Benefit</p>
            <div className="text-base font-bold text-gray-500">
              ${formatToCompactNumber(currentYearDisabilityBenefit)}
            </div>
          </div>

          <Chart
            type="line"
            height="200px"
            width="503px"
            data={disabilityBenefitChart}
            options={lineChartOptions(false, ipTooltipColor, "Plan Year", "Plan Year")}
          />
        </div>

        <div className="w-full mb-3 bg-white">
          <div className="flex justify-between conthrax-font" style={{ width: "503px" }}>
            <p className="font-bold text-gray-800 text-sm">{thirdChartData().label}</p>
            <div className="text-base font-bold text-gray-500">
              ${formatToCompactNumber(thirdChartData().total)}
            </div>
          </div>
          <Chart
            type="line"
            height="200px"
            width="503px"
            data={thirdChartData().chartData}
            options={lineChartOptions(
              false,
              proposalType === ProposalTypes.BuySell ? "#808080" : ipTooltipColor,
              "Plan Year",
              "Plan Year",
            )}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col  h-full relative min-h-screen">
      <div className="relative z-20 px-[4em] w-full max-w-[56em] mx-auto p-[1.75em]">
        {renderContent()}
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <div
          className="h-[350px] bg-cover bg-center"
          style={{
            backgroundImage: `url(${HexagonImg})`,
            backgroundSize: "cover",
          }}
        />
        <div className="px-[4em] w-full max-w-[56em] mx-auto p-[1.75em] pt-1">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IPProjections;
